import { useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../../../lib/db/indexeddb'

export interface ProjectTaskProgress {
  /** Number of completed tasks (status === 'completed', not paused / deleted). */
  done: number
  /** Number of countable tasks (excludes paused / deleted). */
  total: number
  /** `done / total`, or `0` when `total === 0`. Range `[0, 1]`. */
  value: number
}

/**
 * Live progress for the donut indicator on a project's sidebar row.
 *
 * Counting rules (mirrors what the user sees as "open" tasks in the project):
 * - Soft-deleted tasks (`deletedAt != null`) are excluded
 * - Ruhend tasks (`pausedAt != null`) are excluded — they don't count as
 *   open *or* done, matching the Ruhend/Demnächst views
 * - `cancelled` tasks count toward `total` but NOT toward `done` (only
 *   `completed` is "done")
 * - `parentTaskId` is ignored — every task counts 1:1 regardless of nesting
 *
 * Returns `{ done: 0, total: 0, value: 0 }` while the live query is loading
 * or when `projectId` is missing — callers should rely on `total > 0` /
 * `value > 0` to decide visibility, not on a separate "loading" flag.
 */
export function useProjectTaskProgress(projectId: string | undefined): ProjectTaskProgress {
  const tasks = useLiveQuery(
    () => projectId ? db.getTasksByProject(projectId) : undefined,
    [projectId],
  )

  return useMemo<ProjectTaskProgress>(() => {
    if (!tasks) return { done: 0, total: 0, value: 0 }
    let done = 0
    let total = 0
    for (const task of tasks) {
      if (task.deletedAt) continue
      if (task.pausedAt != null) continue
      total++
      if (task.status === 'completed') done++
    }
    const value = total > 0 ? done / total : 0
    return { done, total, value }
  }, [tasks])
}
